/**
 * Production-safe logger utility
 *
 * 開発では詳細を出す。本番では console に詳細を出さない
 * （利用者の端末に内部情報を残さない）。
 *
 * **ただしエラーは捨てない。** 以前ここは本番で
 * `console.error('An error occurred')` とだけ出しており、
 * 実際の内容がどこにも残らなかった。利用者から不具合の連絡を受けても
 * 再現するまで何も分からない状態だった。いまはサーバーに送る。
 */

const isDev = import.meta.env.DEV;

/* errorReporter は services/supabase を読み、supabase は logger を読む。
   静的 import にすると循環参照になるため、必要になった時点で読む。 */
let report: ((kind: 'flow', err: unknown, note?: string) => void) | null = null;
let loading = false;

function forwardToServer(args: unknown[]): void {
  const err = args.find((a) => a instanceof Error) ?? args[0];
  const note = args
    .filter((a) => typeof a === 'string')
    .join(' ')
    .slice(0, 160) || undefined;
  if (report) { report('flow', err, note); return; }
  if (loading) return;
  loading = true;
  import('../services/errorReporter')
    .then((m) => {
      report = m.reportError;
      report('flow', err, note);
    })
    .catch(() => { /* 送れなくても何もしない */ })
    .finally(() => { loading = false; });
}

export const logger = {
  log: (...args: unknown[]) => {
    if (isDev) {
      console.log(...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (isDev) {
      console.warn(...args);
    }
  },
  error: (...args: unknown[]) => {
    if (isDev) {
      console.error(...args);
      return;
    }
    /* 本番: 端末には詳細を出さず、サーバーに残す。 */
    console.error('エラーが発生しました');
    forwardToServer(args);
  },
  debug: (...args: unknown[]) => {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  }
};

export default logger;
